import { Route } from '@angular/router';
import { TodoStateService } from '@app/transactions/todo/state/todo.state.service';
import { CancelledStateService } from '@app/transactions/cancelled/state/cancelled.state.service';
import { InboundInvoicesStateService } from '@app/invoices/inbound/state/inbound-invoices-state.service';
import { OutboundInvoicesStateService } from '@app/invoices/outbound/state/outbound-invoices-state.service';
import { InvoiceNavigationService } from '@app/invoices/shared/services/navigate-to-invoice.service';
import { SelectionService } from '@app/grouped-invoice/shared/services/selection.service';
import { createGroupedInvoiceGuard } from '@app/shared/router-utils/guards/create-grouped-invoice.guard';
import { TransactionNavigationService } from '@app/transactions/shared/services/transaction-details-navigation.service';
import { ROUTE_PARAMS, ROUTE_SEGMENTS } from '@app/app.routes-constants';
import { ExitDialogWarningWithoutUnsaveChanges } from '@app/shared/router-utils/guards/can-deactivate-route.guard';
import { determineDefaultLandingPagePath } from '@app/shared/router-utils/redirect/default-landing-page.redirect';
import { UserDetailsResponse } from '@api-client/mobipay';
import { createErrorRoute } from '@app/shared/utils/route-creators/create-error-route';
import {
  featureAccessCanActivateGuard
} from '@app/shared/features/access-rights/router-utils/guards/feature-access.can-activate.guard';
import {
  invoiceDetailsCanActivateGuard
} from '@app/shared/features/access-rights/router-utils/guards/invoice-details-dynamic-parent-path.guard';
import { ArchiveInvoicesStateService } from '@app/invoices/archived/state/archive-invoices.state.service';
import FeaturesEnum = UserDetailsResponse.FeaturesEnum;

export const appRoutes: Route[] = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: determineDefaultLandingPagePath,
  },
  {
    path: ROUTE_SEGMENTS.TRANSACTIONS,
    loadComponent: () => import('./transactions/transactions.component'),
    providers: [TransactionNavigationService],
    children: [
      {
        path: '',
        redirectTo: ROUTE_SEGMENTS.TODO,
        pathMatch: 'full',
      },
      {
        path: ROUTE_SEGMENTS.TODO,
        loadComponent: () => import('./transactions/todo/todo.component'),
        providers: [TodoStateService],
        canActivate: [
          featureAccessCanActivateGuard({
            requiredFeature: FeaturesEnum.TodoTransactions,
            options: { skipLocationChange: true },
          })
        ],
      },
      {
        path: ROUTE_SEGMENTS.CANCELLED,
        loadComponent: () => import('./transactions/cancelled/cancelled.component'),
        providers: [CancelledStateService],
        canActivate: [
          featureAccessCanActivateGuard({
            requiredFeature: FeaturesEnum.CanceledTransactions,
            options: { skipLocationChange: true },
          })
        ],
      }
    ],
  },
  {
    path: ROUTE_SEGMENTS.TRANSACTION,
    children: [
      {
        path: `${ROUTE_SEGMENTS.EDIT}/:id`,
        loadComponent: () => import('@app/transaction/edit/edit.component'),
        canActivate: [
          featureAccessCanActivateGuard({
            requiredFeature: { allOf: [ FeaturesEnum.TodoTransactions ] },
            options: { skipLocationChange: true },
          })
        ],
      },
      {
        path: `${ROUTE_SEGMENTS.DETAILS}/:id`,
        loadComponent: () => import('@app/transaction/details/transaction-details.component'),
        canActivate: [
          featureAccessCanActivateGuard({
            requiredFeature: { allOf: [ FeaturesEnum.CanceledTransactions ] },
            options: { skipLocationChange: true },
          })
        ],
      },
      {
        path: `${ROUTE_SEGMENTS.EDIT_GROUPED}/:id`,
        loadComponent: () => import('@app/transaction/grouped/edit/edit.component'),
        canActivate: [
          featureAccessCanActivateGuard({
            requiredFeature: { allOf: [
              FeaturesEnum.GroupedInvoicing,
              FeaturesEnum.TodoTransactions
            ] },
            options: { skipLocationChange: true },
          })
        ],
      }
    ],
  },
  {
    path: ROUTE_SEGMENTS.INVOICES,
    loadComponent: () => import('./invoices/invoices.component'),
    providers: [InvoiceNavigationService],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: ROUTE_SEGMENTS.OUTBOUND
      },
      {
        path: ROUTE_SEGMENTS.INBOUND,
        loadComponent: () => import('./invoices/inbound/inbound.component'),
        providers: [InboundInvoicesStateService],
        canActivate: [
          featureAccessCanActivateGuard({
            requiredFeature: FeaturesEnum.InboundInvoices,
            options: { skipLocationChange: true },
          })
        ],
      },
      {
        path: ROUTE_SEGMENTS.OUTBOUND,
        loadComponent: () => import('./invoices/outbound/outbound.component'),
        providers: [OutboundInvoicesStateService],
        canActivate: [
          featureAccessCanActivateGuard({
            requiredFeature: FeaturesEnum.OutboundInvoices,
            options: { skipLocationChange: true },
          })
        ],
      },
      {
        path: ROUTE_SEGMENTS.ARCHIVED,
        loadComponent: () => import('@app/invoices/archived/archived.component'),
        providers: [ArchiveInvoicesStateService],
        canActivate: [
          featureAccessCanActivateGuard({
            requiredFeature: FeaturesEnum.ArchivedInvoices,
            options: { skipLocationChange: true },
          })
        ],
      },
    ],
  },
  {
    /* * **Crucial for Context and Security:** * The `:${ROUTE_PARAMS.INVOICE_ROUTE.PARENT_OVERVIEW}` parameter
     * (e.g., 'inbound' or 'outbound') is **mandatory** for two reasons:
     * * 1. **Return Navigation:** It tells the UI *which* overview page (Inbound or Outbound)
     * to return to after viewing/editing the invoice. The Backend cannot provide this context.
     * 2. **Access Control (`canActivate`):** The `invoiceDetailsCanActivateGuard` **requires** * this parameter to correctly determine if the current user has the
     * `FeaturesEnum.OutboundInvoices` or `FeaturesEnum.InboundInvoices` feature access
     * right before rendering any invoice details/edit views.
     */
    path: `:${ROUTE_PARAMS.INVOICE_ROUTE.PARENT_OVERVIEW}/${ROUTE_SEGMENTS.INVOICE}`,
    canActivate: [invoiceDetailsCanActivateGuard],
    children: [
      {
        path: `${ROUTE_SEGMENTS.EDIT}/:id`,
        loadComponent: () => import('@app/invoice/edit/edit.component'),
      },
      {
        path: `${ROUTE_SEGMENTS.DETAILS}/:id`,
        loadComponent: () => import('@app/invoice/details/details.component'),
      },
      {
        path: `${ROUTE_SEGMENTS.EDIT_GROUPED}/:id`,
        loadComponent: () => import('@app/invoice/grouped/edit/edit.component')
      }
    ],
  },
  {
    path: ROUTE_SEGMENTS.GROUPED_INVOICE,
    providers: [SelectionService],
    canActivate: [
      featureAccessCanActivateGuard({
        requiredFeature: FeaturesEnum.GroupedInvoicing
      })
    ],
    children: [
      {
        path: ROUTE_SEGMENTS.GROUP_TRANSACTIONS,
        loadComponent: () => import('@app/grouped-invoice/group-transactions/group-transactions.component'),
      },
      {
        path: ROUTE_SEGMENTS.CREATE_GROUPED_INVOICE,
        canActivate: [createGroupedInvoiceGuard],
        loadComponent: () => import('@app/grouped-invoice/create/create-grouped-invoice.component'),
      },
    ],
  },
  {
    path: ROUTE_SEGMENTS.CREDIT_NOTE,
    children: [
      {
        path: `${ROUTE_SEGMENTS.CREATE_CREDIT_NOTE}/:linkedInvoiceId`,
        loadComponent: () => import('@app/credit-note/create/create.component'),
        canActivate: [
          featureAccessCanActivateGuard({
            requiredFeature: FeaturesEnum.InvoiceCrediting,
            options: { skipLocationChange: true },
          })
        ],
      },
      {
        path: `${ROUTE_SEGMENTS.EDIT_CREDIT_NOTE_DRAFT}/:invoiceId`,
        loadComponent: () => import('@app/credit-note/edit-draft/edit-credit-note-draft.component')
      },
    ]
  },
  {
    path: ROUTE_SEGMENTS.FREE_INVOICE,
    children: [
      {
        path: `${ROUTE_SEGMENTS.CREATE_FREE_INVOICE}`,
        loadComponent: () => import('@app/free-invoice/create/create-free-invoice.component'),
        canActivate: [
          featureAccessCanActivateGuard({
            requiredFeature: FeaturesEnum.FreeInvoicing,
            options: { skipLocationChange: true },
          })
        ],
        canDeactivate: [ExitDialogWarningWithoutUnsaveChanges('MODALS.FREE_INVOICE.EXIT_WITHOUT_SAVING_WARNING')],
      },
      {
        path: `${ROUTE_SEGMENTS.EDIT_FREE_INVOICE}/:invoiceId`,
        loadComponent: () => import('@app/free-invoice/edit/edit-free-invoice.component'),
        canDeactivate: [ExitDialogWarningWithoutUnsaveChanges('MODALS.FREE_INVOICE.EXIT_WITHOUT_SAVING_WARNING')],
      }
    ]
  },
  createErrorRoute({
    path: ROUTE_SEGMENTS.ERROR_PAGE_STATUS.UNAUTHORIZED,
    data: {
      errorCode: 401,
      title: 'ERROR_PAGE_STATUS.401.TITLE',
      details: 'ERROR_PAGE_STATUS.401.DETAILS'
    }
  }),
  createErrorRoute({
    path: '**',
    data: {
      errorCode: 404,
      title: 'ERROR_PAGE_STATUS.404.TITLE',
      details: 'ERROR_PAGE_STATUS.404.DETAILS'
    }
  }),
];
